import React from "react";
import { motion } from "framer-motion";
import { Brain, Database, Rocket, Zap } from "lucide-react";
import { fadeIn, fadeInUp, fadeInUpSmall } from "../../lib/animations";

const cards = [
  {
    id: "tech-ai",
    icon: Brain,
    title: "AI-Powered Marketing",
    desc: "Advanced machine learning creates high-converting ad copy and optimizes targeting in real-time.",
    gradient: "from-[#0a3d1a] via-[#145a2a] to-[#1a6b35]",
    borderColor: "border-[#39FF14]/30",
    iconColor: "#39FF14",
  },
  {
    id: "tech-mls",
    icon: Database,
    title: "Real-Time MLS Data",
    desc: "Direct integration with MLS systems ensures your ads reflect the latest listing status instantly.",
    gradient: "from-[#2a1a4e] via-[#3b2570] to-[#4a2d8a]",
    borderColor: "border-[#8B5CF6]/30",
    iconColor: "#8B5CF6",
  },
  {
    id: "tech-deploy",
    icon: Rocket,
    title: "Automated Ad Deployment",
    desc: "Launch campaigns in seconds, not days. Our system handles everything from creative to targeting.",
    gradient: "from-[#0a2a4e] via-[#152e5a] to-[#2a1a4e]",
    borderColor: "border-[#00D4FF]/30",
    iconColor: "#00D4FF",
  },
];

const stats = [
  { value: "<60s", label: "AI Lead Response", color: "#39FF14" },
  { value: "24/7", label: "Campaign Monitoring", color: "#39FF14" },
  { value: "1-Click", label: "Meta Launch", color: "#00D4FF" },
  { value: "3%", label: "Managed Ad-Spend Fee", color: "#00D4FF" },
];

export function TechProofSection() {
  return (
    <section className="relative z-10 px-6 md:px-12 lg:px-24 py-20 bg-[#0A0A0F] overflow-hidden">
      {/* Dot pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '32px 32px' }} />

      <div className="max-w-5xl mx-auto relative">
        {/* Badge */}
        <motion.div
          initial={fadeInUpSmall.initial}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          className="text-center mb-6"
        >
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/10 text-gray-300 px-5 py-2 rounded-full text-xs font-semibold tracking-widest uppercase">
            <Zap className="w-3.5 h-3.5 text-[#39FF14]" />
            Powered by Cutting-Edge Technology
          </span>
        </motion.div>

        {/* Heading */}
        <motion.div
          initial={fadeInUpSmall.initial}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Built Different. Built to <span className="text-[#39FF14]">Perform.</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
            Enterprise-grade infrastructure designed specifically for real estate marketing automation
          </p>
        </motion.div>

        {/* 3 Gradient Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {cards.map((card, i) => (
            <motion.div
              key={card.id}
              initial={fadeInUpSmall.initial}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ delay: i * 0.08 }}
              className={`relative rounded-2xl p-6 border ${card.borderColor} bg-gradient-to-br ${card.gradient} backdrop-blur-sm`}
            >
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-16">
                <card.icon className="w-6 h-6" style={{ color: card.iconColor }} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{card.title}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">{card.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={fadeInUpSmall.initial}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-6"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-4xl md:text-5xl font-bold mb-1" style={{ color: stat.color }}>
                {stat.value}
              </p>
              <p className="text-gray-500 text-sm">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        <p className="text-center text-gray-600 text-xs max-w-2xl mx-auto leading-relaxed">
          *AI lead response via Voice AI Nurture on eligible plans. 3% managed ad-spend fee applies to the Market Leader plan — typical agencies charge 10–20% of ad spend.
        </p>
      </div>
    </section>
  );
}
